// services/ordersRouteServce.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('kapta_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isPublic = url.includes('/track');
      if (!isPublic) {
        localStorage.removeItem('kapta_token');
        localStorage.removeItem('kapta_user');
        const path = window.location.pathname;
        if (path.startsWith('/user/')) window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

const wrap = async (promise, fallback = 'Request failed') => {
  try {
    const response = await promise;
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message || error.message || fallback;
    const wrapped = new Error(message);
    wrapped.response = error.response;
    throw wrapped;
  }
};

export const orderService = {
  /* ═══════════════════════════════════════════════════════════════
     USER ORDERS
     ═══════════════════════════════════════════════════════════════ */

  async getMyOrders(params = {}) {
    const { status, paymentStatus, page = 1, limit = 20, sort = 'newest' } =
      params;

    const query = new URLSearchParams();
    if (status) query.append('status', status);
    if (paymentStatus) query.append('paymentStatus', paymentStatus);
    query.append('page', page);
    query.append('limit', limit);
    query.append('sort', sort);

    return wrap(
      api.get(`/api/v1/ordersroute?${query.toString()}`),
      'Failed to get orders'
    );
  },

  async getAllOrders(params = {}) {
    return this.getMyOrders(params);
  },

  async getOrderById(orderId) {
    return wrap(
      api.get(`/api/v1/ordersroute/${orderId}`),
      'Failed to get order'
    );
  },

  async cancelOrder(orderId, reason = '') {
    return wrap(
      api.patch(`/api/v1/ordersroute/${orderId}/cancel`, { reason }),
      'Failed to cancel order'
    );
  },

  /* ═══════════════════════════════════════════════════════════════
     FUTURE GIFT CONTENT
     ═══════════════════════════════════════════════════════════════ */

  /**
   * Get the saved content for one future gift slot.
   * Keyed by the gift's stable `id` (gift.id).
   * Returns: { success, data: content | null }
   */
  async getFutureGiftContent(orderId, giftId) {
    return wrap(
      api.get(`/api/v1/future-gifts/${orderId}/${giftId}`),
      'Failed to get future gift content'
    );
  },

  /**
   * Create or replace the content for one future gift slot.
   * Keyed by the gift's stable `id` (gift.id).
   *
   * @param {string} orderId
   * @param {string} giftId
   * @param {Object} payload
   *
   * ── Digital-memory shape ──
   * @param {string} [payload.pageSlug]
   * @param {string} [payload.memoryTitle]
   * @param {string} [payload.description]
   * @param {Array<{url, publicId?, name?, type?}>} [payload.media]
   * @param {{url, publicId?, name?, duration?}|null} [payload.audioFile]
   * @param {string} [payload.audioUrl]
   * @param {string|Date} [payload.revealDate]
   * @param {string} [payload.revealTime]
   *
   * ── Magnets-frame shape ──
   * @param {Array<{
   *   memoryTitle: string,
   *   memoryStory: string,
   *   uploadedImages: Array<{url, publicId?, name?, type?}>
   * }>} [payload.magnets]
   *
   * ── Audio-gift shape ──
   * @param {string} [payload.giftItem]        - 'teddy-bear' | 'wooden-clock'
   * @param {string} [payload.giftItemLabel]   - 'Teddy Bear' | 'Wooden Alarm Clock'
   * @param {string} [payload.color]
   * @param {string} [payload.personalizedMessage]
   * @param {{url, publicId?, name?, duration?}|null} [payload.audioFile]
   * @param {string} [payload.audioUrl]
   * @param {'upload'|'url'|null} [payload.audioSource]
   */
  async saveFutureGiftContent(orderId, giftId, payload) {
    return wrap(
      api.put(`/api/v1/future-gifts/${orderId}/${giftId}`, payload),
      'Failed to save future gift content'
    );
  },

  /**
   * Convenience alias for digital-memory gifts.
   */
  async saveDigitalMemory(orderId, giftId, payload) {
    return this.saveFutureGiftContent(orderId, giftId, {
      ...payload,
      giftType: 'digital-memory'
    });
  },

  /**
   * Convenience alias for magnets-frame gifts.
   * Expects payload.magnets — the array of magnets built by MagnetsFrameModal.
   */
  async saveMagnetsFrame(orderId, giftId, payload) {
    return this.saveFutureGiftContent(orderId, giftId, {
      ...payload,
      giftType: 'magnets-frame'
    });
  },

  /**
   * 🟢 Convenience alias for audio-gift payloads.
   * Expects the shape produced by AudioGiftModal:
   *   { giftItem, giftItemLabel, color, personalizedMessage, audioFile, audioUrl, audioSource }
   */
  async saveAudioGift(orderId, giftId, payload) {
    return this.saveFutureGiftContent(orderId, giftId, {
      ...payload,
      giftType: 'audio-gift'
    });
  },

  /**
   * List all future gift content belonging to the logged-in user.
   * Returns: { success, data: [...] }
   */
  async getMyFutureGiftContents() {
    return wrap(
      api.get('/api/v1/future-gifts/mine'),
      'Failed to get future gift content'
    );
  }
};

export default orderService;